<template>
  <div :class="$style.picker">
    <div :class="$style.picker__timetable">
      <TimeSlotPickerTimeTable
        v-model="selectedTimeslot"
        @onNextClicked="next"
        @onPrevClicked="prev"
        :columns="timeSlotColumns"
        :hasPrev="hasPrev"
      />
      <div :class="$style.picker__loading" v-show="isLoading">
        <LoadingIndicator />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect, ref } from 'vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import { useAlert } from '@/libs/alertPortal'
import { isSameDay } from 'date-fns'
import { useTimeSlotPickerCalendarAvailableTimeSlotsQuery } from './TimeSlotPicker.generated'
import TimeSlotPickerTimeTable, {
  type Column,
} from './TimeSlotPickerTimeTable.vue'
import type { TimeSlot } from './useTimeSlot.ts'
import { useTimeSlotPickerQueryVars } from './useTimeSlotPickerQueryVars.ts'
import type { TimeSlot as GQLTimeSlot } from '@/types/schema.generated.ts'
import { APIError } from '@/libs/utilities/CustomErrors.ts'

const props = defineProps<{
  modelValue: TimeSlot | undefined
  calendarGuid: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', picked: TimeSlot | undefined): void
}>()

const { error } = useAlert()

const selectedTimeslot = computed({
  get() {
    return props.modelValue
  },
  set(v) {
    emit('update:modelValue', v)
  },
})

const numOfColumns = 4
const { queryVars, daysInRange, hasPrev, next, prev } =
  useTimeSlotPickerQueryVars(numOfColumns, props.calendarGuid)

const query = useTimeSlotPickerCalendarAvailableTimeSlotsQuery(queryVars, {
  fetchPolicy: 'network-only',
})

const isLoading = computed(() => query.loading.value)
const parseTimeSlots = (timeSlots: GQLTimeSlot[]): Column[] => {
  return daysInRange.value.map((d): Column => ({
    date: d,
    availableTimeSlots: (() => {
      if (!timeSlots) {
        return []
      }

      return timeSlots.filter(timeSlot => {
        return isSameDay(new Date(timeSlot.startsAt), d)
      })
    })(),
  }))
}

const timeSlotColumns = ref<Column[]>([])

watchEffect(() => {
  if (query.error.value) {
    error('エラーが発生しました。暫くしてからもう一度お試しください。')
  }

  if (!query.result.value) {
    return
  }

  switch (query.result.value.calendarDetail.__typename) {
    case 'CalendarDetailSuccess':
      break
    case 'CalendarDetailNotFound':
      error('指定された日程調整カレンダーが見つかりません。')
      return
    case 'CalendarDetailPermissionDenied':
      error('指定された日程調整カレンダーへのアクセス権限がありません。')
      return
    default:
      throw new APIError(500, 'Unexpected calendarDetail type')
  }

  const availableTimeSlots =
    query.result.value.calendarDetail.calendar.availableTimeSlots

  switch (availableTimeSlots.__typename) {
    case 'AvailableTimeSlotsErrorNotAvailable':
      error('このカレンダーは現在利用いただけません。設定を見直してください。')
      return
    case 'AvailableTimeSlotsSuccess':
      timeSlotColumns.value = parseTimeSlots(availableTimeSlots.timeSlots)
      return
  }
})
</script>

<style module lang="scss">
.picker {
  width: 100%;

  &__timetable {
    position: relative;
    height: 100%;
    min-height: 500px;
  }

  &__loading {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: $color-surface-primary;
  }
}
</style>
